import type { CabinetConfigurationSnapshot } from './cabinet'

export const APP_VERSION = '0.5.37'
export const PRODUCT_NAME = '必服楼控离线数字展厅'
export const PRODUCT_SCOPE_LABEL = 'DDC/BA 专版'
export const MINIMUM_WINDOWS_VERSION = 'Windows 10'
export const CONTENT_MAINTAINER = '杨宇'

export const contentTypes = [
  'company',
  'product',
  'software-style',
  'software-component',
  'project-case'
] as const

export type ContentType = (typeof contentTypes)[number]

export const systemDomains = ['company', 'ba', 'ddc', 'dcs', 'plc', 'sis'] as const
export type SystemDomain = (typeof systemDomains)[number]

export const rightsLevels = ['public', 'customer-demo', 'employee-only'] as const
export type RightsLevel = (typeof rightsLevels)[number]

export const deliveryChannels = [
  'windows_demo',
  'wecom_h5_internal',
  'solution_preview',
  'pdf_export',
  'image_export'
] as const

export type DeliveryChannel = (typeof deliveryChannels)[number]

export interface PublishedMeta {
  id: string
  contentType: ContentType
  title: string
  summary: string
  systemDomains: SystemDomain[]
  status: 'approved'
  contentVersion: string
  rightsLevel: RightsLevel
  allowedChannels: DeliveryChannel[]
  expiresAt: string | null
  tags: string[]
  sortOrder: number
  mediaIds: string[]
}

export interface CompanyProfile extends PublishedMeta {
  contentType: 'company'
  tagline: string
  facts?: Array<{
    value: string
    label: string
  }>
  values?: string[]
  capabilities: Array<{
    domain: SystemDomain
    title: string
    description: string
  }>
  contact: {
    name?: string
    phone: string
    website?: string
    email: string
    address: string
  }
}

export interface ProductParameter {
  label: string
  value: string
}

export interface Product extends PublishedMeta {
  contentType: 'product'
  productCategory: 'cabinet' | 'ddc-controller' | 'terminal-device'
  model: string
  highlights: string[]
  parameters: ProductParameter[]
  applicationScenarios: string[]
}

export const softwareStylePageKeys = [
  'monitoring',
  'process',
  'group-control',
  'trends',
  'alarms',
  'operations',
  'energy',
  'reports',
  'users',
  'settings'
] as const

export type SoftwareStylePageKey = (typeof softwareStylePageKeys)[number]

export interface SoftwareStylePalette {
  background: string
  surface: string
  primary: string
  accent: string
  text: string
}

export interface SoftwareStylePagePreview {
  pageKey: SoftwareStylePageKey
  pageLabel: string
  mediaId: string
  customerIntro: string
  capabilities: string[]
  customerValue: string
}

export interface SoftwareStyle extends PublishedMeta {
  contentType: 'software-style'
  presentationMode:
    | 'interactive-sample'
    | 'static-introduction'
    | 'style-package'
  styleCode: string
  themeLabel: string
  palette: SoftwareStylePalette
  recommendedFor: string[]
  pagePreviews: SoftwareStylePagePreview[]
  pages: string[]
  highlights: string[]
}

export interface SoftwareComponent extends PublishedMeta {
  contentType: 'software-component'
  problem: string
  value: string
  interactionType: 'status' | 'trend' | 'alarm' | 'schedule' | 'report'
}

export interface ProjectCase extends PublishedMeta {
  contentType: 'project-case'
  buildingType: string
  locationLabel: string
  challenge: string
  solution: string
  projectFacts?: Array<{
    label: string
    value: string
    description?: string
  }>
  controlDescriptions?: Array<{
    system: string
    strategy: string
  }>
  outcomes: string[]
  relatedEntityIds: string[]
  demoData: boolean
}

export type ContentEntity =
  | CompanyProfile
  | Product
  | SoftwareStyle
  | SoftwareComponent
  | ProjectCase

export interface MediaAsset {
  id: string
  mediaType: 'image' | 'video' | 'document'
  path: string
  mimeType: string
  bytes: number
  sha256: string
  altText: string
  rightsLevel: RightsLevel
  allowedChannels: DeliveryChannel[]
  sourceLabel: string
  sourceReference: string
  provenance:
    | 'company-owned'
    | 'authorized-third-party'
    | 'ai-generated'
    | 'unknown'
  conceptVisual: boolean
  pendingReviewFields: string[]
  displayRole:
    | 'cover'
    | 'gallery'
    | 'cabinet-layout'
    | 'thumbnail'
    | 'layer'
    | 'document'
  layoutTemplateId: string | null
  preferred: boolean
}

export interface ContentManifest {
  schemaVersion: number
  contentVersion: string
  buildId: string
  channel: DeliveryChannel
  minAppVersion: string
  generatedAt: string
  demoContent: boolean
  entities: ContentEntity[]
  assets: MediaAsset[]
}

export interface SolutionDraftItem {
  itemId: string
  entityId: string
  contentType: ContentType
  title: string
  configuration?: CabinetConfigurationSnapshot
}

export interface SolutionDraft {
  contentVersion: string
  customerLabel: string
  projectLabel: string
  createdAt: string
  items: SolutionDraftItem[]
}
